import { createSelector } from 'reselect'
import isEmpty from 'lodash/fp/isEmpty'
import curry from 'lodash/fp/curry'
import curryRight from 'lodash/fp/curryRight'
import {
  schema,
  normalize as originalNormalize,
  denormalize as originalDenormalize
} from 'normalizr'
import { selectEntities } from '../state/selectors'

/**
 * curried version of normalize
 * @signature schema -> data -> normalizedData
 * @see {@link https://github.com/paularmstrong/normalizr/blob/master/docs/api.md#normalizedata-schema}
 */
export const normalize = curryRight(originalNormalize)

/**
 * wraps schema in response schema
 *
 * @param {normalizr.schema} schema - schema to wrap in responseSchema
 * @return {normalizr.schema.Object} response schema
 */
export const makeResponseSchema = schema => ({
  response: schema
})

/**
 * creates entity reducer to parse entity date fields
 *
 * @signature ...string -> entity -> entity
 * @param  {...string} dateFields - fields of entity to convert to date
 * @return {Object => Object} function to parse entity dates
 */
export const processEntityDates = (...dateFields) => entity =>
  Object.entries(entity).reduce((processed, [key, value]) => ({
    ...processed,
    [key]: !isEmpty(value) && dateFields.includes(key) ? new Date(value) : value
  }), {})

export const defaultProcessEntityDates = processEntityDates('created_at', 'updated_at')

/**
 * curried version of denormalize
 * change args order
 * @signature schema -> entities -> input
 * @see {@link https://github.com/paularmstrong/normalizr/blob/master/docs/api.md#denormalizeinput-schema-entities}
 */
export const denormalize = curry(
  (schema, entities, input) => originalDenormalize(input, schema, entities)
)

/**
 * creates schemas, normalizers and denormalizers for entity
 *
 * @param {String} entityName - name of the entity
 * @param {Object} [config] - config to create entity schema
 * @param {Object} [config.definitions={}] - definitions of entity schema
 * @param {Boolean} [config.processEntityDates=true] - use default process entity dates strategy
 * @param {Object} [config.options={}] - options for entity schema
 * @return {schemasDefinitions} schemas, normalizers and denormalizers
 * @see {@link https://github.com/paularmstrong/normalizr/blob/master/docs/api.md#entitykey-definition---options--}
 */
export const createEntitySchemas = (entityName, {
  definitions = {},
  processEntityDates = true,
  options = {}
} = {}) => {
  const entity = new schema.Entity(entityName, definitions, {
    processStrategy: processEntityDates ? defaultProcessEntityDates : undefined,
    ...options
  })
  const list = [entity]
  const response = makeResponseSchema(entity)
  const responseList = makeResponseSchema(list)

  return {
    entity,
    list,
    response,
    responseList,
    normalize: normalize(entity),
    normalizeList: normalize(list),
    normalizeResponse: normalize(response),
    normalizeResponseList: normalize(responseList),
    denormalize: denormalize(entity),
    denormalizeList: denormalize(list)
  }
}

/** createEntitiesSelector :: (Fn, Fn) -> Fn -> Object -> a? */
export const createEntitiesSelector = (idsSelector, denormalizer) => createSelector(
  selectEntities,
  idsSelector,
  denormalizer
)

/**
 * @typedef {Object} schemasDefinitions
 * @prop {schema.Entity} entity - entity schema
 * @prop {schema.Array} list - list schema
 * @prop {schema.Object} response - response entity schema
 * @prop {schema.Object} responseList - response list schema
 * @prop {function} normalize - normalize entity function
 * @prop {function} normalizeList - normalize list function
 * @prop {function} normalizeResponse - normalize response function
 * @prop {function} normalizeResponseList - normalize response list function
 * @prop {function} denormalize - denormalize schema function
 * @prop {function} denormalizeList - denormalize list schema function
 */
